import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiArrowLeft,
  FiDownload,
  FiFile,
  FiInbox,
  FiPaperclip,
  FiRefreshCw,
  FiSend,
  FiX,
} from 'react-icons/fi';
import './ChatPage.css';
import { extractOwnerId } from './utils/auth';

const API_BASE_URL = 'https://admin.cgiibnn-esursi.cd/api/candidat';
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt', 'mp3', 'mp4'];
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const getDisplayName = (person) => {
  if (!person) return 'Interlocuteur';
  return person.nom_complet || [person.nom, person.postnom, person.prenom].filter(Boolean).join(' ') || person.email || 'Interlocuteur';
};

const getAttachmentLabel = (message) => {
  const rawLabel = message?.piece_jointe;
  if (!rawLabel || String(rawLabel).startsWith('blob:')) {
    return 'Pièce jointe';
  }

  try {
    const url = new URL(rawLabel);
    const filename = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() || '');
    return filename || 'Pièce jointe';
  } catch {
    const label = String(rawLabel).split('/').filter(Boolean).pop() || '';
    return label.startsWith('http') ? 'Pièce jointe' : label || 'Pièce jointe';
  }
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const sortMessages = (items) => (
  [...items].sort((a, b) => {
    const dateA = new Date(a.date_envoi || 0).getTime();
    const dateB = new Date(b.date_envoi || 0).getTime();
    if (dateA !== dateB) return dateA - dateB;
    return Number(a.id || 0) - Number(b.id || 0);
  })
);

const mergeMessages = (currentMessages, nextMessages) => {
  const messageMap = new Map();
  [...currentMessages, ...nextMessages].forEach((message) => {
    messageMap.set(String(message.id), message);
  });
  return sortMessages(Array.from(messageMap.values()));
};

const ChatPage = ({ user, onBack, onLogout, onUnreadCountChange }) => {
  const currentCandidatId = extractOwnerId(user);
  const isAdmin = user?.est_admin_transfert === true;
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeConversationRef = useRef(null);
  const temporaryUrlsRef = useRef(new Set());
  const lastReadConversationRef = useRef(null);

  const [admins, setAdmins] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [candidateId, setCandidateId] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesRefreshing, setMessagesRefreshing] = useState(false);
  const [conversationsRefreshing, setConversationsRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [retryingMessageId, setRetryingMessageId] = useState(null);
  const [error, setError] = useState('');

  const activeConversationId = selectedConversation?.id;

  const selectedTitle = useMemo(
    () => getDisplayName(selectedConversation?.interlocuteur),
    [selectedConversation]
  );

  const lastMessageId = useMemo(() => {
    const numericIds = messages
      .map((message) => Number(message.id))
      .filter((id) => Number.isFinite(id));
    return numericIds.length > 0 ? Math.max(...numericIds) : null;
  }, [messages]);

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, options);
    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const detail = payload?.message || payload?.detail || `Erreur serveur (${response.status})`;
      throw new Error(detail);
    }

    return payload;
  }, []);

  const loadConversations = useCallback(async (selectFirst = false, silent = false) => {
    if (!currentCandidatId) return [];
    if (silent) setConversationsRefreshing(true);
    try {
      const payload = await fetchJson(`${API_BASE_URL}/chat/conversations/?current_candidat_id=${currentCandidatId}`);
      const results = Array.isArray(payload?.results) ? payload.results : [];
      setConversations(results);
      if (onUnreadCountChange) {
        const totalUnread = results.reduce((total, conversation) => (
          total + Number(conversation.non_lus || 0)
        ), 0);
        onUnreadCountChange(totalUnread);
      }

      setSelectedConversation((current) => {
        if (current && results.some((conversation) => conversation.id === current.id)) {
          return results.find((conversation) => conversation.id === current.id);
        }
        if (selectFirst && results.length > 0) {
          return results[0];
        }
        return current;
      });

      return results;
    } finally {
      if (silent) setConversationsRefreshing(false);
    }
  }, [currentCandidatId, fetchJson, onUnreadCountChange]);

  const createOrGetConversation = useCallback(async (payload) => {
    if (!currentCandidatId) return null;
    const result = await fetchJson(`${API_BASE_URL}/chat/conversations/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        current_candidat_id: currentCandidatId,
        ...payload,
      }),
    });
    const conversation = result?.data || null;
    if (conversation?.id) {
      setSelectedConversation(conversation);
      await loadConversations(false, true);
    }
    return conversation;
  }, [currentCandidatId, fetchJson, loadConversations]);

  const loadInitialData = useCallback(async () => {
    if (!currentCandidatId) {
      setError('Identifiant candidat introuvable.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const existingConversations = await loadConversations(true);

      if (!isAdmin) {
        const adminsPayload = await fetchJson(`${API_BASE_URL}/chat/admins/`);
        const adminResults = Array.isArray(adminsPayload?.results) ? adminsPayload.results : [];
        setAdmins(adminResults);

        if (existingConversations.length === 0 && adminResults.length > 0) {
          await createOrGetConversation({ admin_id: adminResults[0].id });
        }
      }
    } catch (err) {
      setError(err.message || 'Impossible de charger la messagerie.');
    } finally {
      setLoading(false);
    }
  }, [createOrGetConversation, currentCandidatId, fetchJson, isAdmin, loadConversations]);

  const markConversationAsRead = useCallback(async (conversationId) => {
    if (!conversationId || !currentCandidatId) return;
    await fetchJson(`${API_BASE_URL}/chat/conversations/${conversationId}/mark-read/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ current_candidat_id: currentCandidatId }),
    });
  }, [currentCandidatId, fetchJson]);

  const loadMessages = useCallback(async (conversationId, options = {}) => {
    if (!conversationId || !currentCandidatId) return;
    const { afterId = null, silent = false, markAsRead = false } = options;
    if (silent) {
      setMessagesRefreshing(true);
    } else {
      setMessagesLoading(true);
    }
    setError('');
    try {
      const queryParams = new URLSearchParams({
        current_candidat_id: String(currentCandidatId),
      });
      if (afterId) {
        queryParams.set('after_id', String(afterId));
      }

      const payload = await fetchJson(
        `${API_BASE_URL}/chat/conversations/${conversationId}/messages/?${queryParams.toString()}`
      );
      const results = Array.isArray(payload?.results) ? payload.results : [];

      if (activeConversationRef.current !== conversationId) {
        return;
      }

      if (afterId) {
        if (results.length > 0) {
          setMessages((current) => mergeMessages(current, results));
        }
      } else {
        setMessages(sortMessages(results));
      }

      if (markAsRead || results.length > 0) {
        await markConversationAsRead(conversationId);
        lastReadConversationRef.current = conversationId;
        await loadConversations(false, true);
      }
    } catch (err) {
      if (!silent) {
        setError(err.message || 'Impossible de charger les messages.');
      }
    } finally {
      if (silent) {
        setMessagesRefreshing(false);
      } else {
        setMessagesLoading(false);
      }
    }
  }, [currentCandidatId, fetchJson, loadConversations, markConversationAsRead]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!activeConversationId) return undefined;
    activeConversationRef.current = activeConversationId;
    lastReadConversationRef.current = null;
    setMessages([]);
    loadMessages(activeConversationId, { markAsRead: true });
    return undefined;
  }, [activeConversationId, loadMessages]);

  useEffect(() => {
    if (!activeConversationId) return undefined;
    const intervalId = window.setInterval(() => {
      loadMessages(activeConversationId, {
        afterId: lastMessageId,
        silent: true,
        markAsRead: lastReadConversationRef.current !== activeConversationId,
      });
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [activeConversationId, lastMessageId, loadMessages]);

  useEffect(() => {
    const intervalId = window.setInterval(() => loadConversations(false, true), 45000);
    return () => window.clearInterval(intervalId);
  }, [loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeConversationId]);

  const handleSelectAdmin = async (adminId) => {
    if (!adminId) return;
    setError('');
    try {
      await createOrGetConversation({ admin_id: Number(adminId) });
    } catch (err) {
      setError(err.message || 'Impossible de démarrer la conversation.');
    }
  };

  const handleStartAdminConversation = async (event) => {
    event.preventDefault();
    const normalizedCandidateId = Number(candidateId);
    if (!normalizedCandidateId) {
      setError('Veuillez saisir un ID candidat valide.');
      return;
    }

    setError('');
    try {
      await createOrGetConversation({ candidat_id: normalizedCandidateId });
      setCandidateId('');
    } catch (err) {
      setError(err.message || 'Impossible de démarrer la conversation.');
    }
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setError(`Format non autorisé. Formats acceptés : ${ALLOWED_EXTENSIONS.join(', ')}.`);
      event.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('La pièce jointe ne doit pas dépasser 20 MB.');
      event.target.value = '';
      return;
    }

    setError('');
    setAttachment(file);
  };

  const clearAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const revokeTemporaryUrl = useCallback((url) => {
    if (url && temporaryUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      temporaryUrlsRef.current.delete(url);
    }
  }, []);

  useEffect(() => () => {
    temporaryUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    temporaryUrlsRef.current.clear();
  }, []);

  const sendMessagePayload = useCallback(async ({ conversationId, text, file }) => {
    const formData = new FormData();
    formData.append('current_candidat_id', String(currentCandidatId));
    if (text) formData.append('contenu', text);
    if (file) formData.append('piece_jointe', file);

    await fetchJson(`${API_BASE_URL}/chat/conversations/${conversationId}/messages/`, {
      method: 'POST',
      body: formData,
    });
  }, [currentCandidatId, fetchJson]);

  const retryFailedMessage = async (message) => {
    if (!activeConversationId || !message?.retryPayload) return;
    setRetryingMessageId(message.id);
    setError('');

    try {
      await sendMessagePayload({
        conversationId: activeConversationId,
        text: message.retryPayload.text,
        file: message.retryPayload.file,
      });
      revokeTemporaryUrl(message.piece_jointe_url);
      await loadMessages(activeConversationId);
      await loadConversations(false, true);
    } catch (err) {
      setError(err.message || 'Impossible de renvoyer le message.');
    } finally {
      setRetryingMessageId(null);
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    const trimmedContent = content.trim();
    if (!activeConversationId || (!trimmedContent && !attachment)) return;

    const attachmentUrl = attachment ? URL.createObjectURL(attachment) : null;
    if (attachmentUrl) {
      temporaryUrlsRef.current.add(attachmentUrl);
    }

    const temporaryMessage = {
      id: `temp-${Date.now()}`,
      expediteur: {
        id: currentCandidatId,
        nom_complet: getDisplayName(user),
      },
      contenu: trimmedContent,
      piece_jointe_url: attachmentUrl,
      piece_jointe: attachment?.name || null,
      date_envoi: new Date().toISOString(),
      lu: false,
      pending: true,
      failed: false,
      retryPayload: {
        text: trimmedContent,
        file: attachment,
      },
    };

    setMessages((current) => sortMessages([...current, temporaryMessage]));
    setSending(true);
    setError('');

    setContent('');
    clearAttachment();

    try {
      await sendMessagePayload({
        conversationId: activeConversationId,
        text: trimmedContent,
        file: temporaryMessage.retryPayload.file,
      });
      revokeTemporaryUrl(attachmentUrl);
      await loadMessages(activeConversationId);
      await loadConversations(false, true);
    } catch (err) {
      setError(err.message || 'Impossible d’envoyer le message.');
      setMessages((current) => current.map((message) => (
        message.id === temporaryMessage.id
          ? { ...message, pending: false, failed: true }
          : message
      )));
    } finally {
      setSending(false);
    }
  };

  const handleComposerKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!sending && (content.trim() || attachment)) {
        event.currentTarget.form?.requestSubmit();
      }
    }
  };

  if (loading) {
    return (
      <div className="chat-page">
        <div className="chat-loading">Chargement de la messagerie...</div>
      </div>
    );
  }

  return (
    <div className="chat-page">
      <div className="chat-header">
        <div className="chat-header-content">
          <button className="chat-icon-button" onClick={onBack} title="Retour" aria-label="Retour">
            <FiArrowLeft size={20} />
          </button>
          <div className="chat-header-title">
            <h1>Messagerie</h1>
            <span>{isAdmin ? 'Espace administrateur' : 'Assistance transfert'}</span>
          </div>
          <button className="chat-logout-button" onClick={onLogout}>Déconnexion</button>
        </div>
      </div>

      <main className="chat-shell">
        <aside className="chat-sidebar">
          <div className="chat-tools">
            {!isAdmin && admins.length > 0 && (
              <label className="chat-select-label">
                Administrateur
                <select onChange={(event) => handleSelectAdmin(event.target.value)} defaultValue="">
                  <option value="" disabled>Choisir</option>
                  {admins.map((admin) => (
                    <option key={admin.id} value={admin.id}>{getDisplayName(admin)}</option>
                  ))}
                </select>
              </label>
            )}

            {!isAdmin && admins.length === 0 && (
              <div className="chat-tool-note">Aucun administrateur disponible pour le moment.</div>
            )}

            {isAdmin && (
              <form className="chat-start-form" onSubmit={handleStartAdminConversation}>
                {/* <input
                  type="number"
                  min="1"
                  value={candidateId}
                  onChange={(event) => setCandidateId(event.target.value)}
                  placeholder="ID candidat"
                />
                <button type="submit" title="Démarrer une conversation" aria-label="Démarrer une conversation">
                  <FiUserPlus size={18} />
                </button> */}
              </form>
            )}

            <button className="chat-refresh-button" onClick={() => loadInitialData()} title="Actualiser" aria-label="Actualiser">
              <FiRefreshCw size={16} /> {conversationsRefreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
          </div>

          <div className="conversation-list">
            {conversations.length === 0 ? (
              <div className="chat-empty-list">
                <FiInbox size={28} />
                <p>{isAdmin ? 'Aucune conversation' : 'Aucune conversation avec un administrateur'}</p>
              </div>
            ) : conversations.map((conversation) => {
              const isSelected = conversation.id === activeConversationId;
              return (
                <button
                  key={conversation.id}
                  className={`conversation-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedConversation(conversation)}
                >
                  <span className="conversation-avatar">
                    {getDisplayName(conversation.interlocuteur).charAt(0).toUpperCase()}
                  </span>
                  <span className="conversation-content">
                    <span className="conversation-topline">
                      <strong>{getDisplayName(conversation.interlocuteur)}</strong>
                      {conversation.non_lus > 0 && <em>{conversation.non_lus}</em>}
                    </span>
                    <span className="conversation-preview">
                      {conversation.dernier_message?.contenu || (conversation.dernier_message?.piece_jointe_url ? 'Pièce jointe' : 'Aucun message')}
                    </span>
                    <span className="conversation-date">{formatDate(conversation.dernier_message?.date_envoi)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="chat-panel">
          {error && (
            <div className="chat-error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => setError('')} title="Fermer" aria-label="Fermer l’erreur">
                <FiX size={16} />
              </button>
            </div>
          )}

          {selectedConversation ? (
            <>
              <div className="chat-panel-header">
                <span className="chat-avatar-large">{selectedTitle.charAt(0).toUpperCase()}</span>
                <div>
                  <h2>{selectedTitle}</h2>
                  <p>Conversation #{selectedConversation.id}</p>
                </div>
              </div>

              <div className="messages-window">
                {messagesLoading && <div className="messages-loading">Chargement des messages...</div>}
                {messagesRefreshing && !messagesLoading && <div className="messages-refreshing">Synchronisation...</div>}
                {messages.length === 0 && !messagesLoading ? (
                  <div className="messages-empty">Aucun message pour le moment.</div>
                ) : messages.map((message) => {
                  const mine = String(message.expediteur?.id) === String(currentCandidatId);
                  const attachmentUrl = message.piece_jointe_url;
                  return (
                    <div key={message.id} className={`message-row ${mine ? 'mine' : 'theirs'}`}>
                      <div className={`message-bubble ${message.pending ? 'pending' : ''} ${message.failed ? 'failed' : ''}`}>
                        {message.contenu && <p>{message.contenu}</p>}
                        {attachmentUrl && (
                          <a className="message-attachment" href={attachmentUrl} target="_blank" rel="noreferrer">
                            <FiFile size={16} />
                            <span>{getAttachmentLabel(message)}</span>
                            <FiDownload size={15} />
                          </a>
                        )}
                        <span className={`message-meta ${message.failed ? 'failed' : ''}`}>
                          {formatDate(message.date_envoi)}
                          {message.pending ? ' · Envoi...' : ''}
                          {message.failed ? ' · Échec' : ''}
                        </span>
                        {message.failed && (
                          <button
                            type="button"
                            className="retry-message-button"
                            onClick={() => retryFailedMessage(message)}
                            disabled={retryingMessageId === message.id}
                          >
                            {retryingMessageId === message.id ? 'Renvoi...' : 'Réessayer'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {attachment && (
                <div className="attachment-preview">
                  <FiPaperclip size={16} />
                  <span>{attachment.name}</span>
                  <button type="button" onClick={clearAttachment} title="Retirer" aria-label="Retirer la pièce jointe">
                    <FiX size={16} />
                  </button>
                </div>
              )}

              <form className="message-composer" onSubmit={handleSendMessage}>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden-file-input"
                  onChange={handleAttachmentChange}
                  accept={ALLOWED_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
                />
                <button type="button" className="composer-tool" onClick={() => fileInputRef.current?.click()} title="Joindre un fichier" aria-label="Joindre un fichier">
                  <FiPaperclip size={20} />
                </button>
                <textarea
                  value={content}
                  onChange={(event) => setContent(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  placeholder="Écrire un message"
                  rows={1}
                />
                <button
                  type="submit"
                  className="send-button"
                  disabled={sending || (!content.trim() && !attachment)}
                  title="Envoyer"
                  aria-label="Envoyer"
                >
                  <FiSend size={20} />
                </button>
              </form>
            </>
          ) : (
            <div className="chat-empty-panel">
              <FiInbox size={44} />
              <h2>Choisissez une conversation</h2>
              <p>Vos échanges avec l’administration apparaîtront ici.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ChatPage;
